import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Menu, X, Headphones, PackagePlus, ClipboardList,
  Heart, ArrowLeftRight, CreditCard, Home, BookOpen,
  LogOut, User, Shield, Pencil, Check, UserCircle, Eye,
  AlertTriangle, Inbox, MessageCircle, Bell, BellOff,
  Bot, ChevronRight, ChevronLeft,
} from "lucide-react";
import AdminNotificationBadge from "./AdminNotificationBadge";
import { useNavigate, useLocation } from "react-router-dom";
import MegaLogo from "./MegaLogo";
import MyOrdersSidebar from "./MyOrdersSidebar";
import VerifiedBadge from "./VerifiedBadge";
import OwnerBadge from "./OwnerBadge";
import OwnerAvatar from "./OwnerAvatar";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { isVerifiedUser, isOwnerUser } from "../utils/verifiedAccounts";
import { useNotifCounts } from "../hooks/NotificationCountContext";
import "./Navbar.css";

// ── Avatar Component ───────────────────────────────────────────────────────────
const NavAvatar = ({ avatarUrl, letter, color, size = 36, isOwner = false }) => {
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [avatarUrl]);

  if (isOwner) {
    return (
      <OwnerAvatar
        letter={letter}
        avatarUrl={!imgError ? avatarUrl : ""}
        size={size}
        showCrown={true}
      />
    );
  }

  if (avatarUrl && !imgError) {
    return (
      <div
        className="nav-user-avatar nav-user-avatar--img"
        style={{ width: size, height: size, borderColor: color, boxShadow: `0 0 12px ${color}44` }}
      >
        <img src={avatarUrl} alt="avatar" onError={() => setImgError(true)} draggable={false} />
      </div>
    );
  }

  return (
    <div
      className="nav-user-avatar"
      style={{ background: color, boxShadow: `0 0 14px ${color}55` }}
    >
      {letter}
    </div>
  );
};

// ── Edit-Name Modal ────────────────────────────────────────────────────────────
const EditNameModal = ({ isOpen, onClose, currentName, onSave, isAr }) => {
  const [value, setValue] = useState(currentName || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setValue(currentName || "");
      setError("");
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen, currentName]);

  const T = {
    title:       isAr ? "تعديل الاسم"                          : "Edit Name",
    label:       isAr ? "الاسم الجديد"                         : "New Name",
    placeholder: isAr ? "أدخل اسمك الجديد"                    : "Enter your new name",
    save:        isAr ? "حفظ"                                  : "Save",
    saving:      isAr ? "جارٍ الحفظ..."                        : "Saving...",
    cancel:      isAr ? "إلغاء"                                : "Cancel",
    chars:       isAr ? "حرف"                                  : "chars",
    errEmpty:    isAr ? "يرجى إدخال اسم"                       : "Please enter a name",
    errShort:    isAr ? "الاسم قصير جداً (حرفان على الأقل)"    : "Name is too short (min 2 chars)",
    errLong:     isAr ? "الاسم طويل جداً (20 حرفاً كحد أقصى)" : "Name is too long (max 20 chars)",
    errFailed:   isAr ? "حدث خطأ، حاول مرة أخرى"              : "An error occurred, please try again",
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed)           { setError(T.errEmpty);  return; }
    if (trimmed.length < 2) { setError(T.errShort);  return; }
    if (trimmed.length > 20){ setError(T.errLong);   return; }
    setLoading(true);
    setError("");
    try {
      await onSave(trimmed);
      onClose();
    } catch {
      setError(T.errFailed);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="edit-name-backdrop" onClick={onClose} />
      <div className="edit-name-modal" dir={isAr ? "rtl" : "ltr"} role="dialog" aria-modal="true">
        <div className="edit-name-header">
          <span className="edit-name-title-icon"><Pencil size={15} /></span>
          <h2 className="edit-name-title">{T.title}</h2>
          <button className="edit-name-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSave} className="edit-name-form">
          <div className="edit-name-field">
            <label className="edit-name-label">
              {T.label}
              <span className="edit-name-chars" style={{ color: value.length > 18 ? "#e50000" : "#555" }}>
                {value.length}/20 {T.chars}
              </span>
            </label>
            <input
              ref={inputRef}
              type="text"
              className="edit-name-input"
              placeholder={T.placeholder}
              value={value}
              onChange={(e) => { setValue(e.target.value.slice(0, 20)); setError(""); }}
              maxLength={20}
              disabled={loading}
              autoComplete="off"
            />
            {error && <span className="edit-name-error">{error}</span>}
          </div>
          <div className="edit-name-actions">
            <button type="button" className="edit-name-btn edit-name-btn-cancel" onClick={onClose} disabled={loading}>
              {T.cancel}
            </button>
            <button type="submit" className="edit-name-btn edit-name-btn-save" disabled={loading || !value.trim()}>
              {loading
                ? <svg className="edit-name-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                : <Check size={14} />}
              {loading ? T.saving : T.save}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

// ── Main Navbar (Top Bar Only) ─────────────────────────────────────────────────
const Navbar = ({ onCurrencyToggle }) => {
  const [scrolled, setScrolled]         = useState(false);
  const [myOrdersOpen, setMyOrdersOpen] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [nameSaved, setNameSaved]       = useState(false);
  // drawer state — kept for compatibility (no longer used for More button)
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [openSection, setOpenSection]   = useState(null);

  const navigate  = useNavigate();
  const location  = useLocation();
  const { lang, setLang, t } = useLanguage();
  const { user, username, isGuest, logout, updateUsername, profile } = useAuth();
  const isAr       = lang === "ar";
  const isVerified = isVerifiedUser(user?.uid);
  const isOwner    = isOwnerUser(user?.uid);

  const { counts, markSeen, chatTotal } = useNotifCounts();

  const [notifStatus, setNotifStatus] = useState(() => {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission;
  });

  const [myOrdersCount, setMyOrdersCount] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("myProofIds") || "[]").length;
    } catch { return 0; }
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
    setOpenSection(null);
  }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  useEffect(() => {
    const checkCount = () => {
      try {
        setMyOrdersCount(JSON.parse(localStorage.getItem("myProofIds") || "[]").length);
      } catch {}
    };
    window.addEventListener("storage", checkCount);
    const interval = setInterval(checkCount, 3000);
    return () => { window.removeEventListener("storage", checkCount); clearInterval(interval); };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if ("Notification" in window) setNotifStatus(Notification.permission);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const toggleLanguage = () => {
    setLang(lang === "ar" ? "en" : "ar");
    setDrawerOpen(false);
    setOpenSection(null);
  };

  const handleLogout = async () => {
    setDrawerOpen(false);
    setOpenSection(null);
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {}
  };

  const handleSaveName = async (newName) => {
    await updateUsername(newName);
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2000);
  };

  const avatarLetter = (username || "?").charAt(0).toUpperCase();
  const avatarColor  = (() => {
    try { return localStorage.getItem("user_avatar_color") || "#e50000"; } catch { return "#e50000"; }
  })();
  const avatarUrl = profile?.avatarUrl || "";

  // ── Drawer sections (less-used items) ───────────────────────────────────────
  const drawerSections = [
    {
      id: "main",
      labelAr: "الرئيسية", labelEn: "Home",
      icon: <Home size={15} />, color: "#e50000",
      items: [
        {
          labelAr: "الصفحة الرئيسية", labelEn: "Home Page",
          icon: <Home size={14} />,
          action: () => { navigate("/"); setDrawerOpen(false); },
        },
        {
          labelAr: "الفيديو التعليمي", labelEn: "Tutorial Video",
          icon: <BookOpen size={14} />,
          action: () => { navigate("/Educationalvideo"); setDrawerOpen(false); },
        },
      ],
    },
    {
      id: "client",
      labelAr: "خدمات العميل", labelEn: "Client Services",
      icon: <Headphones size={15} />, color: "#0088cc",
      badge: counts.supportReplies > 0 ? counts.supportReplies : null,
      items: [
        {
          labelAr: "محوّل العملات", labelEn: "Currency Converter",
          icon: <ArrowLeftRight size={14} />, color: "#ffbb00",
          action: () => { onCurrencyToggle(); setDrawerOpen(false); },
        },
        {
          labelAr: "طلبات خاصة", labelEn: "Special Requests",
          icon: <PackagePlus size={14} />, color: "#e50000",
          action: () => { navigate("/specialrequest"); setDrawerOpen(false); },
        },
        {
          labelAr: "الدعم الفني", labelEn: "Technical Support",
          icon: <Headphones size={14} />, color: "#0088cc",
          badge: counts.supportReplies > 0 ? counts.supportReplies : null,
          action: () => { navigate("/support"); markSeen("supportReplies"); setDrawerOpen(false); },
        },
        {
          labelAr: "إرسال شكوى", labelEn: "Send a Complaint",
          icon: <AlertTriangle size={14} />, color: "#ff6b35",
          action: () => { navigate("/send-complaint"); setDrawerOpen(false); },
        },
      ],
    },
    // Mailbox (owner only)
    ...(isOwner ? [{
      id: "mailbox",
      labelAr: "البريد", labelEn: "Mailbox",
      icon: <Inbox size={15} />, color: "#a855f7",
      badge: (counts.support + counts.complaints) > 0 ? (counts.support + counts.complaints) : null,
      items: [
        {
          labelAr: "بريد الشكاوي", labelEn: "Complaints Inbox",
          icon: <AlertTriangle size={14} />, color: "#ff6b35",
          badge: counts.complaints > 0 ? counts.complaints : null,
          action: () => { navigate("/mailbox/complaints"); markSeen("complaints"); setDrawerOpen(false); },
        },
        {
          labelAr: "بريد الدعم", labelEn: "Support Inbox",
          icon: <Headphones size={14} />, color: "#0088cc",
          badge: counts.support > 0 ? counts.support : null,
          action: () => { navigate("/mailbox/support"); markSeen("support"); setDrawerOpen(false); },
        },
      ],
    }] : []),
    {
      id: "orders",
      labelAr: "الطلبات والدفع", labelEn: "Orders & Payment",
      icon: <ClipboardList size={15} />, color: "#ffbb00",
      badge: (myOrdersCount > 0 || (isOwner && counts.payments > 0))
        ? (isOwner && counts.payments > 0 ? counts.payments : myOrdersCount) : null,
      items: [
        {
          labelAr: "طلباتي", labelEn: "My Orders",
          icon: <ClipboardList size={14} />, color: "#ffbb00",
          badge: myOrdersCount > 0 ? myOrdersCount : null,
          action: () => { setMyOrdersOpen(true); setDrawerOpen(false); },
        },
        {
          labelAr: "وسائل الدفع", labelEn: "Payment Methods",
          icon: <CreditCard size={14} />, color: "#25D366",
          action: () => { navigate("/payment"); setDrawerOpen(false); },
        },
        ...(isOwner ? [{
          labelAr: "دفعات معلقة", labelEn: "Pending Payments",
          icon: <CreditCard size={14} />, color: "#e50000",
          badge: counts.payments > 0 ? counts.payments : null,
          action: () => { navigate("/adminpanel/pendingpayments"); markSeen("payments"); setDrawerOpen(false); },
        }] : []),
      ],
    },
    {
      id: "support",
      labelAr: "ادعمنا", labelEn: "Support Us",
      icon: <Heart size={15} />, color: "#ff4d6d",
      items: [
        {
          labelAr: "ادعم Leaks Mercy", labelEn: "Support Leaks Mercy",
          icon: <Heart size={14} />, color: "#ff4d6d",
          action: () => { navigate("/Supportus"); setDrawerOpen(false); },
        },
      ],
    },
  ];

  // Total badge for drawer button (all unread counts)
  const totalDrawerBadge = counts.supportReplies + counts.support + counts.complaints + counts.payments + myOrdersCount;

  return (
    <>
      {/* ══════════════════════════════════════════════════════════
          TOP NAVBAR
      ══════════════════════════════════════════════════════════ */}
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="navbar-container">
          {/* ── Left: Logo ── */}
          <div
            className="navbar-center"
            onClick={() => navigate("/")}
            style={{ cursor: "pointer" }}
          >
            <MegaLogo size={38} />
          </div>

          {/* ── Right: Language toggle ── */}
          <div className="navbar-right">
            <button
              className="navbar-lang-flag"
              onClick={toggleLanguage}
              title={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
              type="button"
              aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
            >
              {lang === "ar" ? (
                <span className="navbar-flag-emoji" role="img" aria-label="English">🇬🇧</span>
              ) : (
                <span className="navbar-flag-emoji" role="img" aria-label="العربية">🇸🇦</span>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════
          BOTTOM NAV BAR
      ══════════════════════════════════════════════════════════ */}
      <nav className={`bottom-nav${isAr ? " bottom-nav--rtl" : ""}`} aria-label={isAr ? "التنقل الرئيسي" : "Main Navigation"}>

        {/* 1. Home */}
        <button
          className={`bnav-item${location.pathname === "/" ? " bnav-item--active" : ""}`}
          onClick={() => navigate("/")}
          aria-label={isAr ? "الرئيسية" : "Home"}
        >
          <div className="bnav-icon-wrap">
            <Home size={22} />
          </div>
          <span className="bnav-label">{isAr ? "الرئيسية" : "Home"}</span>
        </button>

        {/* 2. Chat */}
        <button
          className={`bnav-item${(location.pathname.startsWith("/chats") || location.pathname === "/friends" || location.pathname.startsWith("/privatechat")) ? " bnav-item--active" : ""}`}
          onClick={() => navigate("/chats/public")}
          aria-label={isAr ? "الدردشة" : "Chat"}
        >
          <div className="bnav-icon-wrap bnav-badge-wrap">
            <MessageCircle size={22} />
            {chatTotal > 0 && (
              <span className="bnav-badge">{chatTotal > 99 ? "99+" : chatTotal}</span>
            )}
          </div>
          <span className="bnav-label">{isAr ? "الدردشة" : "Chat"}</span>
        </button>

        {/* 3. AI Support */}
        <button
          className={`bnav-item${location.pathname === "/AiSupport" ? " bnav-item--active" : ""}${isGuest ? " bnav-item--guest-locked" : ""}`}
          onClick={() => isGuest ? navigate("/pleasesignup") : navigate("/AiSupport")}
          aria-label={isAr ? "الدعم الذكي" : "AI Support"}
        >
          <div className={`bnav-icon-wrap${isGuest ? " bnav-icon--locked" : ""}`}>
            <Bot size={22} />
          </div>
          <span className="bnav-label">{isAr ? "المساعد" : "Assistant"}</span>
        </button>

        {/* 4. Profile */}
        <button
          className={`bnav-item${(location.pathname === "/ViewProfile" || location.pathname === "/EditProfile") ? " bnav-item--active" : ""}${isGuest ? " bnav-item--guest-locked" : ""}`}
          onClick={() => isGuest ? navigate("/pleasesignup") : navigate("/ViewProfile")}
          aria-label={isAr ? "الملف الشخصي" : "Profile"}
        >
          <div className={`bnav-icon-wrap${isGuest ? " bnav-icon--locked" : ""}`}>
            {avatarUrl
              ? <img src={avatarUrl} alt="" className="bnav-avatar-img" />
              : <UserCircle size={22} />
            }
          </div>
          <span className="bnav-label">{isAr ? "حسابي" : "Profile"}</span>
        </button>

        {/* 5. Notifications */}
        <button
          className={`bnav-item${location.pathname === "/Customizenotifications" ? " bnav-item--active" : ""}${isGuest ? " bnav-item--guest-locked" : ""}`}
          onClick={() => isGuest ? navigate("/pleasesignup") : navigate("/Customizenotifications")}
          aria-label={isAr ? "الإشعارات" : "Notifications"}
        >
          <div className={`bnav-icon-wrap bnav-badge-wrap${isGuest ? " bnav-icon--locked" : ""}`}>
            {notifStatus === "granted" ? <Bell size={22} /> : <BellOff size={22} />}
            {!isGuest && notifStatus !== "granted" && notifStatus !== "unsupported" && (
              <span className="bnav-dot" />
            )}
          </div>
          <span className="bnav-label">{isAr ? "إشعارات" : "Notifs"}</span>
        </button>

        {/* 6. More → /settings (page route مستقل بدلاً من Drawer) */}
        <button
          className={`bnav-item bnav-item--more${location.pathname === "/settings" ? " bnav-item--active" : ""}`}
          onClick={() => navigate("/settings")}
          aria-label={isAr ? "المزيد" : "More"}
        >
          <div className="bnav-icon-wrap bnav-badge-wrap">
            <Menu size={22} />
            {totalDrawerBadge > 0 && (
              <span className="bnav-badge">{totalDrawerBadge > 99 ? "99+" : totalDrawerBadge}</span>
            )}
          </div>
          <span className="bnav-label">{isAr ? "المزيد" : "More"}</span>
        </button>
      </nav>

      {/* Drawer مُعطّل — تم استبداله بصفحة /settings المستقلة */}

      {/* My Orders Sidebar */}
      <MyOrdersSidebar
        isOpen={myOrdersOpen}
        onClose={() => setMyOrdersOpen(false)}
      />

      {/* Admin Background Notification Listener */}
      {isOwner && <AdminNotificationBadge isAr={isAr} />}

      {/* Edit Name Modal */}
      {!isGuest && (
        <EditNameModal
          isOpen={editNameOpen}
          onClose={() => setEditNameOpen(false)}
          currentName={username}
          onSave={handleSaveName}
          isAr={isAr}
        />
      )}
    </>
  );
};

export default Navbar;
